// Browser-side Web Push helper (Session AFF). Wraps the Notification / PushManager / service-worker
// dance so the engineer PWA (and the office messages page) can register for push with one call.
// Every function is window-guarded and jsdom-safe: with no service worker / PushManager (test env,
// old browser, iOS without a home-screen install) isPushSupported() is false and the helpers no-op.
import { api } from "./api";
import { announcePhoneViewWriteBlocked, isInPhonePreviewFrame } from "./phonePreview";

// True when this browser can do Web Push at all.
export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// Convert a VAPID public key (URL-safe base64) to the byte array the PushManager wants as its
// applicationServerKey. Backed by an explicit ArrayBuffer so the result is a Uint8Array<ArrayBuffer>
// (not <ArrayBufferLike>), which lib.dom's BufferSource / applicationServerKey requires under the
// generic-typed-array lib. Standard incantation; unit-tested against known vectors.
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const output = new Uint8Array(new ArrayBuffer(rawData.length));
  for (let i = 0; i < rawData.length; i += 1) output[i] = rawData.charCodeAt(i);
  return output;
}

// The current PushSubscription for this browser, if any (so the UI can tell "already subscribed").
export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

// Session ANZ: "dismissed" is a THIRD outcome, not a flavour of "denied". Notification.permission
// stays "default" when the user closes the prompt without choosing, and telling that person "your
// browser blocked notifications for this site, change it in site settings" is simply wrong advice --
// nothing is blocked, and the setting they would be sent to look at does not exist yet. They can
// just be asked again.
export type SubscribeResult = { ok: true } | { ok: false; reason: "unsupported" | "denied" | "dismissed" | "error" };

// Ask permission, create (or reuse) the browser subscription, and register it server-side. Returns
// a discriminated result so the banner can react (disappear on success, remember a denial). Never
// throws.
export async function subscribeToPush(publicKey: string): Promise<SubscribeResult> {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  // The Phone view (Session BDZ) shares this browser's push subscription with the real window: turning notifications on
  // there would ask for permission and subscribe the REAL window with no server row to match. Not from the frame.
  if (isInPhonePreviewFrame()) {
    announcePhoneViewWriteBlocked();
    return { ok: false, reason: "error" };
  }
  try {
    const permission = await Notification.requestPermission();
    if (permission === "denied") return { ok: false, reason: "denied" };
    if (permission !== "granted") return { ok: false, reason: "dismissed" };
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ??
      (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
    const json = sub.toJSON();
    await api.push.subscribe({
      endpoint: sub.endpoint,
      keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    });
    setPushOptOut(false); // turning it on is the engineer's own choice too
    return { ok: true };
  } catch {
    return { ok: false, reason: "error" };
  }
}

// Session PN1: true when a subscription was made with this VAPID public key (or its key cannot be read, which is treated as a match:
// an unreadable key is not a reason to throw a working subscription away).
export function subscriptionMatchesKey(sub: { options?: { applicationServerKey?: ArrayBuffer | null } }, publicKey: string): boolean {
  const have = sub.options?.applicationServerKey;
  if (!have) return true;
  const a = new Uint8Array(have);
  const b = urlBase64ToUint8Array(publicKey);
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export type EnsureResult = "registered" | "not_granted" | "opted_out" | "unsupported" | "error";

// Session PN1 (review finding 1): an engineer's DELIBERATE "turn off" must stick. Permission stays "granted" after an unsubscribe, so
// without a memory the next app open would silently subscribe again. This flag is the engineer's own choice, kept on the device
// (localStorage, try/catch: it can come back empty, in which case the silent re-registration simply runs). It is set by the explicit
// controls (the Home On/Off row, the sign-out tick) and cleared by Enable / Turn on; a permission revoked in the phone's settings is NOT
// an opt-out and never sets it.
const OPT_OUT_KEY = "ele.engineerPushOptOut";
export function isPushOptedOut(): boolean {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}
export function setPushOptOut(on: boolean): void {
  try {
    if (typeof window === "undefined") return;
    if (on) window.localStorage.setItem(OPT_OUT_KEY, "1");
    else window.localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    /* best effort */
  }
}

// Session PN1: SILENT re-registration. A phone's registration belongs to the DEVICE and the engineer, not to the session, so it
// outlives a session that ran out. It can still be LOST (a cleared browser, a reinstall, a changed VAPID key, a row the server
// dropped on a 404/410, or a rotation that happened while signed out and so could not be posted). When permission is still granted
// this re-creates and re-posts it WITHOUT asking anything, on every app open: the server's upsert by endpoint makes it idempotent,
// and it re-homes the phone to whoever is signed in now. It never prompts: with permission not granted it does nothing.
export async function ensurePushRegistered(publicKey: string): Promise<EnsureResult> {
  if (!isPushSupported()) return "unsupported";
  if (isInPhonePreviewFrame()) return "error";
  if (pushPermission() !== "granted") return "not_granted";
  if (isPushOptedOut()) return "opted_out";
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (sub && !subscriptionMatchesKey(sub, publicKey)) {
      const staleEndpoint = sub.endpoint;
      await sub.unsubscribe().catch(() => undefined);
      await api.push.unsubscribe(staleEndpoint).catch(() => undefined);
      sub = null;
    }
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
    const json = sub.toJSON();
    await api.push.subscribe({
      endpoint: sub.endpoint,
      keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    });
    return "registered";
  } catch {
    return "error";
  }
}

// Session PN1: an iPhone only does web push for an app ADDED TO THE HOME SCREEN (iOS 16.4 or later); in Safari's own tab the
// browser has no PushManager at all, so isPushSupported() is false and the engineer was shown nothing. These two let the banner
// say what to do instead of staying silent.
export function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as a Mac with a touch screen.
  return /Macintosh/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if ((navigator as unknown as { standalone?: boolean }).standalone === true) return true;
    return typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches;
  } catch {
    return false;
  }
}

// Remove this browser's subscription locally AND server-side (the user-facing unsubscribe). Best
// effort throughout.
export async function unsubscribePush(): Promise<void> {
  if (!isPushSupported()) return;
  // Turning notifications off in the Phone view would unsubscribe the REAL window's shared subscription (the entrapment and
  // SLA alerts) while the server still holds the endpoint. Not from the frame.
  if (isInPhonePreviewFrame()) {
    announcePhoneViewWriteBlocked();
    return;
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const { endpoint } = sub;
    await sub.unsubscribe().catch(() => undefined);
    await api.push.unsubscribe(endpoint).catch(() => undefined);
  } catch {
    /* best effort */
  }
}

// ---- Permission state, and reconciling a REVOKED permission (Session ANZ) ----
//
// THE CASE THIS CLOSES. An engineer taps Enable, grants permission, and a PushSubscription row is
// stored server-side. Weeks later they turn notifications off for the site in Android settings. The
// browser stops showing anything, but the SERVER still holds the row and still counts them as
// reachable -- so the standby sweep records "1 device notified" for a phone that displayed nothing,
// and the office has no way to know the on-call engineer never saw it.
//
// The server cannot detect this on its own: a revoked permission is a client-side fact, and the push
// service does not always return 410 for it (the subscription can remain technically valid while the
// browser suppresses display). The only place that knows is the browser, so the reconciliation has to
// live here and run when the app opens.
//
// It is deliberately CONSERVATIVE about what it tears down: only when permission is actually
// "denied" or has been reset to "default" while a subscription still exists. A "granted" permission
// is left completely alone.

export type PushPermissionState = "unsupported" | "granted" | "denied" | "default";

/** The browser's current notification permission, normalised and jsdom-safe. */
export function pushPermission(): PushPermissionState {
  if (!isPushSupported()) return "unsupported";
  try {
    const p = Notification.permission;
    return p === "granted" || p === "denied" ? p : "default";
  } catch {
    return "unsupported";
  }
}

export type ReconcileResult = "unsupported" | "no_subscription" | "granted" | "revoked_cleared";

/**
 * Bring the stored subscription back into line with the browser's CURRENT permission. Call this on
 * app open (the engineer PWA home and the office settings page both do).
 *
 * Returns what it found, so a caller can show the right thing: "revoked_cleared" means the user had
 * turned notifications off behind our back and the now-useless subscription has been removed both
 * locally and server-side, which is what puts the opt-in control back in front of them.
 *
 * Never throws.
 */
export async function reconcilePushPermission(): Promise<ReconcileResult> {
  if (!isPushSupported()) return "unsupported";
  try {
    const existing = await getExistingSubscription();
    if (!existing) return "no_subscription";
    if (pushPermission() === "granted") return "granted";
    // Permission was revoked or reset while we held a subscription. Remove it rather than leaving a
    // row the server would count as a reachable device for ever.
    await unsubscribePush();
    return "revoked_cleared";
  } catch {
    return "no_subscription";
  }
}

// Listen for the service worker's navigate hint (posted from notificationclick) and hand the path to
// the caller (PushBanner wires this to react-router navigate). Returns an unsubscribe fn. The SW
// also posts the plain string "ele-flush" for background-sync; that is ignored here (it has no
// `type`). No-op when there is no service worker (test env).
export function registerPushNavigationHandler(onNavigate: (path: string) => void): () => void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return () => undefined;
  const listener = (event: MessageEvent) => {
    const data = event.data as { type?: string; path?: string } | undefined;
    if (data && data.type === "navigate" && typeof data.path === "string") onNavigate(data.path);
  };
  navigator.serviceWorker.addEventListener("message", listener);
  return () => navigator.serviceWorker.removeEventListener("message", listener);
}
